import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import BulletinLikeHandler from '../BulletinLikeHandler';
import BulletinImage from '../BulletinImage';
import { useAuth } from '@/contexts/AuthContext';

interface RelicStageOneProps {
  bulletin: any;
  onClose: () => void;
  onLikeEngagement: () => void;
  hasUserLiked: boolean;
  setHasUserLiked: (liked: boolean) => void;
  setCurrentStage: (stage: 1 | 2 | 3) => void;
}

const RelicStageOne: React.FC<RelicStageOneProps> = ({
  bulletin,
  onClose,
  onLikeEngagement,
  hasUserLiked,
  setHasUserLiked,
  setCurrentStage
}) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <BulletinLikeHandler
      bulletinId={bulletin.id}
      initialLikeCount={bulletin.like_count || 0}
      userId={user?.id}
      currentImageUrl={bulletin.image_url}
      currentThumbnailUrl={bulletin.thumbnail_url}
    >
      {(hasLiked, currentLikeCount, handleDoubleClick) => {
        // If user has already liked, go directly to stage 2
        React.useEffect(() => {
          if (hasLiked && !hasUserLiked) {
            setHasUserLiked(true);
            setCurrentStage(2);
          }
        }, [hasLiked]);

        const handleLikeClick = (e: React.MouseEvent) => {
          if (!hasLiked) {
            handleDoubleClick(e);
            onLikeEngagement();
          }
        };

        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="relative w-full h-full flex items-center justify-center"
          >
            {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white border border-white/20"
            style={{ backdropFilter: 'blur(8px)' }}
          >
              <X className="h-5 w-5" />
            </Button>

            {/* Image container (expand to viewport) */}
            <div 
              className="relative w-full h-full flex items-center justify-center"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh'
              }}
            >
              <BulletinImage
                src={bulletin.image_url}
                isSelected={true}
                onClick={() => {}}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                size="large"
                isMature={bulletin.is_mature}
                showMatureContent={true}
              />
              
              {/* Like button overlay */}
              {!hasLiked && (
                <Button
                  onClick={handleLikeClick}
                  className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-md transition-all duration-200 px-8 py-3 shadow-lg"
                >
                  <Heart className="h-5 w-5 mr-2" />
                  Like
                </Button>
              )}
            </div>
          </motion.div>
        );
      }}
    </BulletinLikeHandler>
  );
};

export default RelicStageOne;